import { Add } from '@mui/icons-material';
import { Alert, Button, Dialog, DialogTitle, FormControlLabel, Grid, InputAdornment, Switch, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { AppModels, AppState, UserPhoneAttrs, brandNewUserPhone, hasErrors, saveModel, userPhoneSelector, userPhoneSlice, userPhoneValidator, userSelector, userSlice } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { appStore } from '../../store';

const UserPhoneForm = (props: { attrs: UserPhoneAttrs, defaultIsDefault: boolean, isDefault: boolean, setIsDefault: (p: boolean) => void, update: (p: Partial<UserPhoneAttrs>) => void; }) => {
    return <Grid container padding={2} spacing={2}>
        {props.attrs.__err && <Alert color='error'>{props.attrs.__err}</Alert>}
        <Grid item xs={12}>
            <Box sx={{ display: 'flex' }}>

                <TextField
                    sx={{ flexGrow: 1, mr: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <Add />
                            </InputAdornment>
                        )
                    }}
                    fullWidth
                    label='Numéro de téléphone'
                    placeholder='19991234567'

                    value={props.attrs.phone}
                    onChange={(event) => {
                        props.update({ phone: event.target.value });
                    }}
                    error={!!props.attrs.__errors?.phone}
                    helperText={props.attrs.__errors?.phone} />
                <TextField
                    sx={{ width: '120px' }}
                    fullWidth
                    label='Ext.'
                    placeholder='000'

                    value={props.attrs.ext}
                    onChange={(event) => {
                        props.update({ ext: event.target.value });
                        if (event.target.value !== '') {
                            props.update({ isMobile: false });
                        }

                    }}
                    error={!!props.attrs.__errors?.ext}
                    helperText={props.attrs.__errors?.ext} />

            </Box>
        </Grid>
        <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={props.attrs.isMobile === true} onChange={() => { props.update({ isMobile: !props.attrs.isMobile }); }} />} label="Appareil mobile (pouvant recevoir SMS)" />
        </Grid>
        <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={props.attrs.type == 'emergency'} onChange={() => { props.update({ type: (props.attrs.type == 'contact' ? 'emergency' : 'contact') }); }} />} label="Numéro en cas d'urgence (visible à tous les employés)" />
        </Grid>
        <Grid item xs={12}>
            <TextField
                fullWidth
                label='Étiquette'
                value={props.attrs.caption}
                onChange={(event) => {
                    props.update({ caption: event.target.value });
                }}
                error={!!props.attrs?.__errors?.caption}
                helperText={props.attrs.__errors?.caption} />
        </Grid>
        {props.defaultIsDefault === false && <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={props.isDefault} onChange={() => { props.setIsDefault(!props.isDefault); }} />} label='Mobile par défaut (SMS)' />
        </Grid>}
    </Grid>;
};

export const NewUserPhone = (props: { defaults?: Partial<UserPhoneAttrs>, onClose: () => void; }) => {

    const [editingUserPhone, setEditingUserPhone] = React.useState<UserPhoneAttrs | undefined>(undefined);

    const user = useSelector((state: AppState) => editingUserPhone ? userSelector.selectById(state, editingUserPhone.userId) : undefined);

    const [isDefault, setIsDefault] = React.useState(false);

    React.useEffect(() => {

        setEditingUserPhone(props.defaults ? { ...brandNewUserPhone, ...props.defaults } : undefined);
    }, [props.defaults]);

    const onSave = async () => {
        if (editingUserPhone) {
            const validatedUserPhone = await userPhoneValidator.validate(editingUserPhone);
            if (!hasErrors(validatedUserPhone)) {

                const result = await saveModel(AppModels.userPhones, editingUserPhone);
                setEditingUserPhone(result);
                if (!hasErrors(result)) {
                    appStore.dispatch(userPhoneSlice.actions.addOne(result));
                    if (user && isDefault) {
                        const userResult = await saveModel(AppModels.users, {
                            id: user.id,
                            phoneId: result.id
                        });
                        appStore.dispatch(userSlice.actions.updateOne({ id: userResult.id, changes: userResult }));
                    }
                    props.onClose();
                }
            } else {
                setEditingUserPhone(validatedUserPhone);
            }
        }
    };

    if (!editingUserPhone) return <></>;

    const update = (p: Partial<UserPhoneAttrs>) => {
        setEditingUserPhone(q => {
            return q ? { ...q, ...p } : undefined;
        }
        );
    };

    return <><Dialog
        open={!!editingUserPhone}
        onClose={props.onClose}>
        <DialogTitle>Nouvelle adresse courriel</DialogTitle>
        <UserPhoneForm attrs={editingUserPhone} update={update} defaultIsDefault={false} isDefault={isDefault} setIsDefault={setIsDefault} />
        <Grid container padding={2} spacing={2}>
            <Grid item xs={12}>
                <Button onClick={onSave} color='primary'>Ajouter</Button>
                <Button onClick={props.onClose}>Annuler</Button>
            </Grid>
        </Grid>
    </Dialog></>;
};

export const UserPhoneEditor = (props: { id?: ModelId, onClose: () => void; }) => {

    const [editingUserPhone, setEditingUserPhone] = React.useState<UserPhoneAttrs | undefined>();
    const userPhone = useSelector((state: AppState) => props.id ? userPhoneSelector.selectById(state, props.id) : undefined);
    const user = useSelector((state: AppState) => editingUserPhone ? userSelector.selectById(state, editingUserPhone.userId) : undefined);

    const [updated, setUpdated] = React.useState(false);

    const [isDefault, setIsDefault] = React.useState(false);
    const [defaultIsDefault, setDefaultIsDefault] = React.useState(false);

    React.useEffect(() => {
        if (userPhone) {
            setEditingUserPhone(userPhone);
            setIsDefault(!!user && !!userPhone && userPhone.id === user.phoneId);
            setDefaultIsDefault(!!user && !!userPhone && userPhone.id === user.phoneId);
        }
    }, [userPhone, user]);

    React.useEffect(() => {
        setUpdated(JSON.stringify(editingUserPhone) != JSON.stringify(userPhone) || isDefault !== defaultIsDefault);
    }, [editingUserPhone, userPhone, isDefault]);

    const checkSetDefault = async (userPhoneId: ModelId) => {
        if (user && isDefault && user.phoneId !== userPhoneId) {
            const userResult = await saveModel(AppModels.users, {
                id: user.id,
                phoneId: userPhoneId
            });
            appStore.dispatch(userSlice.actions.updateOne({ id: userResult.id, changes: userResult }));
        }
    };

    const onSave = async () => {
        if (editingUserPhone) {
            if (updated) {
                if (JSON.stringify(editingUserPhone) != JSON.stringify(userPhone)) {
                    const validatedUserPhone = await userPhoneValidator.validate(editingUserPhone);
                    if (!hasErrors(validatedUserPhone)) {
                        const result: UserPhoneAttrs = await saveModel(AppModels.userPhones, editingUserPhone);
                        setEditingUserPhone(result);
                        if (!hasErrors(result)) {
                            appStore.dispatch(userPhoneSlice.actions.updateOne({ id: editingUserPhone.id, changes: result }));
                            await checkSetDefault(result.id);
                            props.onClose();
                        }

                    }
                } else if (isDefault !== defaultIsDefault) {
                    await checkSetDefault(editingUserPhone.id);
                    props.onClose();
                }
            }
        };
    };

    if (!editingUserPhone) return <></>;

    const update = (p: Partial<UserPhoneAttrs>) => {
        setEditingUserPhone(q => {
            return q ? { ...q, ...p } : undefined;
        }
        );
    };

    return <><Dialog
        open={!!userPhone}
        onClose={props.onClose}>
        <DialogTitle>Modifier numéro de téléphone</DialogTitle>
        <UserPhoneForm attrs={editingUserPhone} update={update} defaultIsDefault={defaultIsDefault} isDefault={isDefault} setIsDefault={setIsDefault} />
        <Grid container padding={2} spacing={2}>
            <Grid item xs={12}>
                <Button disabled={!updated} onClick={onSave} color='primary'>Sauvegarder</Button>
                <Button onClick={props.onClose}>Annuler</Button>
            </Grid>
        </Grid>
    </Dialog></>;
};
